/**
 * checkBacklogCoverage — pure fn.
 *
 * Given a set of eligible topics + a list of sprint output-theme quotas,
 * compute whether the backlog satisfies every sprint's requirement at the
 * 85% coverage floor (same tolerance as validateThemeCoverage).
 *
 * Returns per-theme gaps so UI can show "need N more {theme}" hints and
 * the Stage B dialog can build an auto-fill request with the right
 * themeDistribution.
 *
 * Used in two places:
 *   1. Backend — StrategyPlanningOrchestrator Phase 4 (decides whether to
 *      abort or dispatch auto-fill).
 *   2. Frontend — CreateContentStrategyDialog backlog readiness panel
 *      (live-recomputes whenever user changes targetPlatforms / horizon).
 *
 * Pure: no Firestore, no AI, no side effects.
 */

import type { ContentTopic, ContentSprint } from '../types';
import { resolveThemeId } from './themeKeys';

export interface SprintQuotaPlanEntry {
  sprintType: string;
  dateStart: string;
  dateEnd: string;
  /** themeId → required post count for this sprint (integer) */
  themeQuotas: Record<string, number>;
}

export interface BacklogCoverageInput {
  /** Eligible topics from project backlog (status ∈ {ready, evergreen}) */
  eligibleTopics: ContentTopic[];
  /** Pre-computed sprint skeletons or Phase-0 quota plan */
  sprintQuotaPlan: SprintQuotaPlanEntry[];
  /** Under-coverage tolerance (default 0.85) */
  underTolerance?: number;
}

export interface ThemeGap {
  themeId: string;
  /** Weighted sum of backlog topics that declare this theme (weight × count) */
  actualWeight: number;
  /** Sum of requiredCount across all sprints for this theme */
  targetCount: number;
  /** actualWeight / targetCount — 0..∞ */
  deltaRatio: number;
  /** Rounded shortfall, how many more whole topics needed to reach target */
  missingCount: number;
}

export interface BacklogCoverageResult {
  /** True when every theme crosses the underTolerance threshold */
  ok: boolean;
  /** Only themes that failed — empty array when ok=true */
  gaps: ThemeGap[];
  /** Raw weighted sums per theme (for debug + UI tooltips) */
  weightedSums: Record<string, number>;
  /** Overall ratio: total actualWeight / total targetCount (0..∞) */
  overallCoverage: number;
}

/**
 * Aggregate sprint quotas → total required count per theme across the plan.
 * E.g. if sprint1 needs 6 educational + sprint2 needs 10 educational,
 *      total educational target = 16.
 */
function aggregateQuotas(plan: SprintQuotaPlanEntry[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const sprint of plan) {
    for (const [themeId, required] of Object.entries(sprint.themeQuotas)) {
      totals[themeId] = (totals[themeId] ?? 0) + required;
    }
  }
  return totals;
}

/**
 * Walk topic.themeAssignment entries, summing `weight` per themeId.
 * Matches the weighting rule used by validateThemeCoverage — a topic
 * with themeAssignment=[{themeId:edu, weight:0.7}, {themeId:contrarian,
 * weight:0.3}] contributes 0.7 toward edu + 0.3 toward contrarian.
 */
function weightedSumsByTheme(topics: ContentTopic[]): Record<string, number> {
  const sums: Record<string, number> = {};
  for (const topic of topics) {
    const assignment = (topic.themeAssignment ?? []) as Array<{
      themeId?: string;
      theme?: string;
      weight?: number;
    }>;
    for (const entry of assignment) {
      const themeId = resolveThemeId(entry);
      if (!themeId) continue;
      const w = entry.weight ?? 0;
      sums[themeId] = (sums[themeId] ?? 0) + w;
    }
  }
  return sums;
}

export function checkBacklogCoverage(input: BacklogCoverageInput): BacklogCoverageResult {
  const underTol = input.underTolerance ?? 0.85;
  const totals = aggregateQuotas(input.sprintQuotaPlan);
  const sums = weightedSumsByTheme(input.eligibleTopics);

  const gaps: ThemeGap[] = [];
  let totalRequired = 0;
  let totalActual = 0;

  for (const [themeId, target] of Object.entries(totals)) {
    totalRequired += target;
    const actual = sums[themeId] ?? 0;
    totalActual += actual;
    if (target === 0) continue;
    const ratio = actual / target;
    if (ratio < underTol) {
      // Clamp to ≥1. Defensive: floating-point aggregation of fractional
      // themeAssignment weights can push `target - actual` to values that
      // round to 0 (e.g. 0.2 + 0.8 = 1.0000000000000002), which downstream
      // causes buildGapFillBrief.totalCount to be 0 and auto-fill to
      // request zero topics — a nonsensical state we never want to emit.
      const rawMissing = Math.ceil(target - actual);
      gaps.push({
        themeId,
        actualWeight: actual,
        targetCount: target,
        deltaRatio: ratio,
        missingCount: Math.max(1, rawMissing),
      });
    }
  }

  const overallCoverage = totalRequired === 0 ? 1 : totalActual / totalRequired;
  return {
    ok: gaps.length === 0,
    gaps,
    weightedSums: sums,
    overallCoverage,
  };
}

/**
 * Helper for the dialog's "Auto-fill gaps" button — converts ThemeGap[]
 * into a TopicGenerationBrief-compatible {themeIds, themeDistribution}.
 * Distribution weighted by missingCount so generation batches the right
 * ratio.
 */
export function buildGapFillBrief(gaps: ThemeGap[]): {
  themeIds: string[];
  themeDistribution: Record<string, number>;
  totalCount: number;
} {
  const themeIds = gaps.map((g) => g.themeId);
  // Floor totalMissing to max(themeIds.length, sum) — guarantees at least
  // one topic per gap theme, even if individual missingCount values ended
  // up at 0 through exotic floating-point paths. Without this floor
  // StrategyPlanningOrchestrator would dispatch Stage A with count: 0
  // and the user would see "Стратегии требуется 0 топиков" paradox.
  const rawSum = gaps.reduce((s, g) => s + g.missingCount, 0);
  const totalMissing = Math.max(rawSum, themeIds.length);
  const themeDistribution: Record<string, number> = {};
  for (const g of gaps) {
    themeDistribution[g.themeId] = totalMissing === 0 ? 0 : g.missingCount / totalMissing;
  }
  return { themeIds, themeDistribution, totalCount: totalMissing };
}

/**
 * Overload for ContentSprint[] — when the caller has sprint docs with
 * sprintContract.outputThemeQuotas already populated, it extracts the
 * plan automatically.
 */
export function sprintsToQuotaPlan(sprints: ContentSprint[]): SprintQuotaPlanEntry[] {
  return sprints.map((s) => {
    const quotas = ((s.sprintContract?.outputThemeQuotas ?? []) as Array<{
      themeId?: string;
      theme?: string;
      requiredCount: number;
    }>).reduce<Record<string, number>>((acc, q) => {
      const themeId = resolveThemeId(q);
      if (themeId) acc[themeId] = q.requiredCount;
      return acc;
    }, {});
    return {
      sprintType: (s.sprintType ?? '') as string,
      dateStart: String(s.dateStart ?? ''),
      dateEnd: String(s.dateEnd ?? ''),
      themeQuotas: quotas,
    };
  });
}
